import { getRailsUpdate } from '@/common/utils/getRailsUpdate'
import { mapToDeletion } from '@/common/utils/railsHelpers'
import { unFormatCurrency, unFormatUnknownUnit } from '@/common/utils/unFormatters'
import {
  GovernmentAllocationEntryProductCategoryCurrencyValidationSchema,
  GovernmentAllocationEntryProductCategoryQuantityValidationSchema,
} from '@/features/privateCompanies/pages/CompanyGovernmentAllocations/schemas/update.schema'
import {
  transformNewProduct,
  transformNewProductQuantity,
  transformUpdatedProduct,
  transformUpdatedProductQuantity,
} from '@/features/privateCompanies/pages/CompanyGovernmentAllocations/Update/utils/transformNewProduct'
import { CompanyGovernmentAllocationEntryProductCategory } from '@/features/privateCompanies/types/companyGovernmentAllocationEntry.type'

type FormProductCategory = GovernmentAllocationEntryProductCategoryCurrencyValidationSchema | GovernmentAllocationEntryProductCategoryQuantityValidationSchema

const isQuantityForm = (form: FormProductCategory): form is GovernmentAllocationEntryProductCategoryQuantityValidationSchema => {
  return 'total_quantity' in form
}

const getExistingProductId = (
  productId: string,
  existingProducts: { id: string; product: { id: string } }[],
) => {
  return existingProducts.find((p) => p.product.id === productId)?.id
}

export const transformNewProductCategory = (
  formProductCategory: FormProductCategory,
  productCategory: CompanyGovernmentAllocationEntryProductCategory,
  governmentAllocationCurrencyRestricted: boolean | undefined,
) => {
  const existingProducts = productCategory?.entry_products || []
  const existingProductIds = existingProducts.map((p) => p.product.id)

  if (!isQuantityForm(formProductCategory)) {
    return {
      product_category_id: formProductCategory.product_category.id,
      total_value: formProductCategory.total_value ? unFormatCurrency(formProductCategory.total_value) : 0,
      entry_products_attributes: [
        ...getRailsUpdate(
          existingProducts,
          formProductCategory.entry_products || [],
          (x) => x.product.id,
          (x) => x.product.id,
          mapToDeletion,
          (product) => (product.id ? transformUpdatedProduct(product) : transformNewProduct(product)),
        ),
        ...(formProductCategory.entry_products
          ?.filter((product) => product.id || existingProductIds.includes(product.product.id))
          ?.map((product) => {
            const id = product.id || getExistingProductId(product.product.id, existingProducts)
            return transformUpdatedProduct({ ...product, id })
          }) || []),
      ],
    }
  }

  return {
    product_category_id: formProductCategory.product_category.id,
    total_quantity: formProductCategory.total_quantity ? Number(unFormatUnknownUnit(formProductCategory.total_quantity)) || 0 : 0,
    total_value:
      governmentAllocationCurrencyRestricted
        ? formProductCategory.total_value ? Number(unFormatCurrency(formProductCategory.total_value)) || 0 : 0
        : undefined,
    entry_products_attributes: [
      ...getRailsUpdate(
        existingProducts,
        formProductCategory.entry_products || [],
        (x) => x.product.id,
        (x) => x.product.id,
        mapToDeletion,
        (product) => (product.id ? transformUpdatedProductQuantity(product, governmentAllocationCurrencyRestricted) : transformNewProductQuantity(product, governmentAllocationCurrencyRestricted)),
      ),
      ...(formProductCategory.entry_products
        ?.filter((product) => product.id || existingProductIds.includes(product.product.id))
        ?.map((product) => {
          const id = product.id || getExistingProductId(product.product.id, existingProducts)
          return transformUpdatedProductQuantity({ ...product, id }, governmentAllocationCurrencyRestricted)
        }) || []),
    ],
  }
}

export const transformUpdatedProductCategory = (
  formProductCategory: FormProductCategory,
  productCategory: CompanyGovernmentAllocationEntryProductCategory,
  governmentAllocationCurrencyRestricted: boolean | undefined,
) => {
  const { product_category_id, ...transformedProductCategory } = transformNewProductCategory(
    formProductCategory,
    productCategory,
    governmentAllocationCurrencyRestricted,
  )

  return {
    id: formProductCategory.id,
    ...transformedProductCategory,
  }
}
